import * as React from 'react';
import { SpinLoader } from 'app/components';
import { STATE } from 'app/constants';
import { deepEquals } from 'app/infrastructure/js';
import { containerFactory, DefaultContainerProps } from 'app/infrastructure/redux';
import { hasTranslationsLoaded } from 'app/infrastructure/translate/selectors';
import { SearchFilters } from 'app/area/search-filters/search-filters';
import { SerializePayload } from 'app/area/search-filters/types';
import { FilterSearchModel } from 'app/area/search-filters/types-store';
import { searchTranslations } from '../constants';
import { SearchResult } from '../search-container/search-result';
import { actionCreators } from '../actions';
import { AdministrationSearchState, SearchArea } from '../types-store';
import { UserTile } from './user-tile';
import { UserToolbar } from './user-toolbar';
import { userFilters, predefinedFilters, userQueryFilters, defaultFilters } from './user-filter-configuration';
import { serialize } from '../serialize/serialize';
import { SearchTitle } from 'app/area/search-filters/search-container/search-title';
import {
  extraInfoDefaultFilters, extraInfoFilters, shouldLoadExtraInfo
} from 'app/area/administration/list/filters/extra-info-filters';
import { scrollToTop } from 'app/infrastructure/platform/routing';

type UserSearchProps = DefaultContainerProps & AdministrationSearchState & typeof actionCreators;

class UserSearch extends React.Component<UserSearchProps> {

  public static statePath: string = STATE.administrationSearch;

  public componentWillUnmount() {
    this.props.clearList();
  }

  public componentDidMount() {
    this.loadExtraInfo();
    this.props.checkRoles();
    scrollToTop();
  }

  private loadExtraInfo = () => {
    shouldLoadExtraInfo(this.props) && this.props.loadExtraInfo();
  };

  private updateSearchModel = (payload: SerializePayload) => {
    this.onLoadData(serialize(payload));
  };

  private onLoadData = (searchModel: FilterSearchModel, startIndex?: number) => {

    const { loadUsers, requestedSearchModel } = this.props;

    if (startIndex !== undefined || !deepEquals(searchModel, requestedSearchModel)) {
      loadUsers(searchModel, startIndex);
    }
  };

  private onFetchMore = () => {

    const { results, requestedSearchModel } = this.props;

    if (results.fetching || !results.list || results.list.length === 0 || results.list.length === results.total) {
      return;
    }

    this.onLoadData(requestedSearchModel, results.list.length);
  };

  private onDelete = (id: number) => {

    const { deleteUser, translation } = this.props;

    deleteUser(id, translation.user_successfully_deleted);
  };

  private onExportToExcel = () => {
    const {
      results: {
        total
      }, translation
    } = this.props;

    total > 0 && this.props.userExportToExcel(this.props.requestedSearchModel, total,
      translation.user_excel_started, translation.user_excel_success);
  };

  private getDefaultFilterValues = () => {
    const { extraInfoCodes, translation } = this.props;
    return { ...defaultFilters(translation), ...extraInfoDefaultFilters(extraInfoCodes, translation) };
  };

  private getInitialDefaultFilterValues = () => {
    const { predefined_include, predefined_exclude, ...initialDefaultFilterValues } = this.getDefaultFilterValues();
    return initialDefaultFilterValues;
  };

  public render() {

    const { results, translation, sharedState, loadUserAccess, clearPersonAccess,
      exportUserExcelFetching, extraInfoCodes, fetchingExtraInfoCodes,
      hasRoles, fetchingRoles,
      sharedState: { access: { adminPanel: { user: {
        create: createUserAccess = false,
        read: readUserAccess = false,
        administrationCentral: administrateAccess = false,
        mfaRecovery = false
      } = {}, experienceTransferAlertsPanel, restrictedAccessPanel } = {} } = {} } = {},
      requestedSearchModel: { defaultValue: { codetable = [] } = {} } = {} } = this.props;

    if (fetchingExtraInfoCodes || fetchingRoles || !hasTranslationsLoaded(translation)) {
      return <SpinLoader />;
    }

    const userToolbar = <UserToolbar
      onExportToExcel={this.onExportToExcel}
      exportExcelFetching={exportUserExcelFetching}
      totalResultHits={results.total}
      translation={translation}
      canCreate={createUserAccess}
      canRead={readUserAccess}
      canRecoverMfa={administrateAccess && mfaRecovery}
    />;

    const enableAlternativeId = !!sharedState.access && !!sharedState.access.adminPanel &&
      !!sharedState.access.adminPanel.alternativeIdPanel;

    return <SearchFilters
      topToolbar={<SearchTitle title={translation.user_administration} />}
      rightToolbar={userToolbar}
      filtersConfig={{ area: SearchArea.user }}
      queryFilters={userQueryFilters(translation, enableAlternativeId)}
      predefinedFilters={predefinedFilters(translation, enableAlternativeId)}
      customFilters={extraInfoFilters(
        userFilters({
          hasExperienceTransferAlertsAccess: experienceTransferAlertsPanel,
          hasRestrictedAccess: restrictedAccessPanel,
          hasRoles, readOnlyDefaultFilterPickers: codetable.length === 0
        }, translation),
        extraInfoCodes, { shouldDisplay: hasRoles, title: translation.exclude_inherited_from_role }
      )}
      defaultFilterValues={this.getDefaultFilterValues()}
      initialDefaultFilterValues={this.getInitialDefaultFilterValues()}
      updateSearchModel={this.updateSearchModel}>
      <SearchResult
        resultTile={UserTile}
        results={results}
        noHitLabel={translation.search_no_users}
        canDuplicate={createUserAccess}
        showMeatball={administrateAccess || createUserAccess}
        translation={translation}
        onFetchMore={this.onFetchMore}
        onDelete={this.onDelete}
        onLoadPersonAccess={loadUserAccess}
        onClearPersonAccess={clearPersonAccess} />
    </SearchFilters>;
  }
}

const container = containerFactory(UserSearch, actionCreators, undefined, searchTranslations);

export { container as UsersSearchView };
